import { cva, type VariantProps } from 'class-variance-authority'
import { icons as lucideIcons } from 'lucide-react'
import { memo } from 'react'
import { cn } from '../../../lib/utils'
import { SELECT_ICONS } from './select-icons'

const iconVariants = cva('size-4 stroke-2', {
  variants: {
    variant: {
      default: 'text-header',
      positionLeft: 'size-5 mr-2 text-primary-accent',
      positionRight: 'size-5 ml-2 text-primary-accent',
      center: 'mx-auto size-5 text-primary-accent',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const ICONS = {
  ...lucideIcons,
  ...SELECT_ICONS,
} as const

export type IconName = keyof typeof ICONS

// IconProps typing is split into IconPropsWithoutName and IconProps to avoid a circular type issue:
// - Custom select icons (e.g. TriangleAlertFilled) use IconPropsWithoutName
// - Custom select icons are added to SELECT_ICONS, which is spread into ICONS alongside lucide icons
// - IconName is derived from the keys in ICONS
// - Including `name: IconName` in the custom select icon props would create a circular dependency
export type IconPropsWithoutName = {
  className?: string
  strokeWidth?: number
} & Omit<React.SVGProps<SVGSVGElement>, 'className'> &
  VariantProps<typeof iconVariants>

export type IconProps = IconPropsWithoutName & { name: IconName }

export const Icon = memo(({ name, className, strokeWidth, variant, ...props }: IconProps) => {
  const IconComponent = ICONS[name]

  if (!IconComponent) {
    return null
  }

  return (
    <IconComponent {...props} className={cn(iconVariants({ variant }), className)} strokeWidth={strokeWidth ?? 2.5} />
  )
})

Icon.displayName = 'Icon'
